import { useEffect, useMemo, useState } from 'react';

export interface MediaSlide {
  kind: 'photo' | 'video';
  url: string;
}

export interface UseMediaGalleryResult {
  slides: MediaSlide[];
  index: number;
  current: MediaSlide | undefined;
  hasMultiple: boolean;
  onPrev: () => void;
  onNext: () => void;
  onSelect: (index: number) => void;
}

/**
 * Paging state for a photo/video strip.
 *
 * `resetKey` (the listing id) matters: without it, opening a 5-photo listing at index 4 and then
 * a 1-photo one leaves the gallery pointing at a slide that no longer exists.
 */
export const useMediaGallery = (
  photos: string[],
  video: string | undefined,
  resetKey: string | undefined,
): UseMediaGalleryResult => {
  const slides = useMemo<MediaSlide[]>(
    () => [
      ...photos.map((url) => ({ kind: 'photo' as const, url })),
      ...(video ? [{ kind: 'video' as const, url: video }] : []),
    ],
    [photos, video],
  );

  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
  }, [resetKey]);

  const safeIndex = slides.length === 0 ? 0 : Math.min(index, slides.length - 1);

  return {
    slides,
    index: safeIndex,
    current: slides[safeIndex],
    hasMultiple: slides.length > 1,
    onPrev: () => setIndex((i) => (i <= 0 ? slides.length - 1 : i - 1)),
    onNext: () => setIndex((i) => (i >= slides.length - 1 ? 0 : i + 1)),
    onSelect: setIndex,
  };
};
